import {
  CarIcon,
  ListChecksIcon,
  PlusIcon,
  ShoppingCartIcon,
} from 'lucide-react'
import type { DraggableAttributes } from '@dnd-kit/core'
import type { CSSProperties, ReactNode } from 'react'

import { Link } from '../lib/Link'
import type { Order } from './types'

type DragHandleProps = {
  attributes?: DraggableAttributes
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  listeners?: Record<string, any>
  style?: CSSProperties
  setActivatorNodeRef?: (element: HTMLElement | null) => void
}

export default function DbOrderHeader({
  order,
  summary,
  dragHandle,
}: {
  order: Partial<Order>
  summary?: ReactNode
  /** When set, the title is the drag handle for reordering lists. */
  dragHandle?: DragHandleProps
}) {
  const id = order.id ? 'order-' + order.id : 'home'

  const header = (
    <>
      {order.display}
      <div>
        {summary && <div className="text-sm opacity-60 mt-1">{summary}</div>}
      </div>
    </>
  )

  const titleClass =
    'flex-1 min-w-0 font-semibold text-3xl opacity-40 touch-manipulation'

  return (
    <div className="mb-4" id={id}>
      <div className="flex gap-2 sm:gap-4 items-start">
        {order.id ? (
          <Link
            className={`${titleClass} ${dragHandle ? 'cursor-grab active:cursor-grabbing' : ''}`}
            href={'?order=' + order.id}
            ref={dragHandle?.setActivatorNodeRef}
            style={dragHandle?.style}
            {...(dragHandle?.attributes ?? {})}
            {...(dragHandle?.listeners ?? {})}
          >
            {header}
          </Link>
        ) : (
          <div className={titleClass}>{header}</div>
        )}

        <Link
          className="p-3 rounded hover:bg-white hover:text-bg focus-visible:bg-white focus-visible:text-bg outline-hidden"
          href={'?order=' + order.id}
        >
          {order.deliveredAt ? (
            <ListChecksIcon className="w-6 h-6" />
          ) : order.orderedAt ? (
            <CarIcon className="w-6 h-6" />
          ) : order.id ? (
            <ShoppingCartIcon className="w-6 h-6" />
          ) : null}
        </Link>

        <Link
          href={{ query: { orderAdd: order.id || 'home' } }}
          className="p-3 bg-white/20 rounded hover:bg-white hover:text-bg outline-hidden focus-visible:bg-white focus-visible:text-bg"
        >
          <PlusIcon className="w-6 h-6" />
        </Link>
      </div>
    </div>
  )
}
